import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UnderlyingDataState {
  data: any[];
  rawResponse: any; // Store the full API response
  loading: boolean;
  error: string | null;
  activeTab: string;
  dimensionNames: string[];
  tabs: {
    id: string;
    name: string;
    data: any[];
    loading: boolean;
    error: string | null;
  }[];
}

const initialState: UnderlyingDataState = {
  data: [],
  rawResponse: null,
  loading: false,
  error: null,
  activeTab: '',
  dimensionNames: [],
  tabs: [],
};

const underlyingDataSlice = createSlice({
  name: 'underlyingData',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setData: (state, action: PayloadAction<any[]>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    setRawResponse: (state, action: PayloadAction<any>) => {
      state.rawResponse = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setTabData: (state, action: PayloadAction<{ tabId: string; data: any[] }>) => {
      const tab = state.tabs.find(t => t.id === action.payload.tabId);
      if (tab) {
        tab.data = action.payload.data;
        tab.loading = false;
        tab.error = null;
      }
    },
    setTabLoading: (state, action: PayloadAction<{ tabId: string; loading: boolean }>) => {
      const tab = state.tabs.find(t => t.id === action.payload.tabId);
      if (tab) {
        tab.loading = action.payload.loading;
      }
    },
    setTabError: (state, action: PayloadAction<{ tabId: string; error: string | null }>) => {
      const tab = state.tabs.find(t => t.id === action.payload.tabId);
      if (tab) {
        tab.error = action.payload.error;
        tab.loading = false;
      }
    },
    setDimensionNames: (state, action: PayloadAction<string[]>) => {
      state.dimensionNames = action.payload;
      // Create tabs dynamically based on dimension names
      state.tabs = action.payload.map((dimensionName, index) => ({
        id: `tab-${index + 1}`,
        name: dimensionName,
        data: [],
        loading: false,
        error: null,
      }));
      // Set first tab as active if no active tab is set
      if (action.payload.length > 0 && !state.activeTab) {
        state.activeTab = `tab-1`;
      }
    },
    clearData: (state) => {
      state.data = [];
      state.rawResponse = null;
      state.loading = false;
      state.error = null;
      state.dimensionNames = [];
      state.tabs = [];
      state.activeTab = '';
    },
  },
});

export const {
  setLoading,
  setData,
  setRawResponse,
  setError,
  setActiveTab,
  setTabData,
  setTabLoading,
  setTabError,
  setDimensionNames,
  clearData,
} = underlyingDataSlice.actions;

export default underlyingDataSlice.reducer;
